// The hosted OBS chat overlay page: streamnook.app/overlay/<id>.
// A streamer pastes this URL into an OBS/StreamElements/Streamlabs Browser Source.
// It runs with the desktop app closed — it loads the published config by id, then
// (next phase) opens a WebSocket to the ingestion service and renders the merged
// multi-platform chat through the same renderer the in-app builder previews.
//
// Chromeless + transparent by design: no site header/footer/background, so it
// composites cleanly over the scene. Mounted OUTSIDE the <Layout> route group.

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { HostedOverlayFeed } from '../overlay/HostedOverlayFeed';
import { OverlayChat } from '../overlay/OverlayChat';
import { clampOverlayStyle, DEFAULT_OVERLAY_STYLE } from '../overlay/overlayConfig';
import { DEMO_MESSAGES } from '../overlay/demoMessages';

interface ChannelRef {
  provider: string;
  channel: string;
}

interface OverlayConfig {
  id: string;
  channels: ChannelRef[];
  style: Record<string, unknown>;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; config: OverlayConfig };

export default function Overlay() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const demo = searchParams.get('demo') === '1';
  const live = searchParams.get('live'); // diagnostic: ?live=<twitchChannel>
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  // Force a transparent page while this overlay is mounted, so OBS composites it
  // over the scene instead of over the site's dark canvas. Restored on unmount.
  useEffect(() => {
    const htmlPrev = document.documentElement.style.background;
    const bodyPrev = document.body.style.background;
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    return () => {
      document.documentElement.style.background = htmlPrev;
      document.body.style.background = bodyPrev;
    };
  }, []);

  useEffect(() => {
    if (demo || live) return; // demo/live modes bypass the config fetch
    if (!id) {
      setState({ kind: 'error', message: 'No overlay id in the URL.' });
      return;
    }
    let cancelled = false;
    let lastJson = '';
    const load = async () => {
      try {
        // Cache-bust so the poll sees fresh config (the GET is edge-cached ~30s),
        // making the open overlay a LIVE MIRROR of the builder.
        const res = await fetch(`/api/overlays/${encodeURIComponent(id)}?t=${Date.now()}`);
        if (res.status === 404) {
          if (!cancelled && !lastJson) setState({ kind: 'error', message: 'This overlay was not found or has been disabled.' });
          return;
        }
        if (!res.ok) throw new Error(`Failed to load overlay (HTTP ${res.status}).`);
        const config = (await res.json()) as OverlayConfig;
        const json = JSON.stringify(config);
        // Only update on an actual change, so an unchanged poll never churns render.
        if (!cancelled && json !== lastJson) {
          lastJson = json;
          setState({ kind: 'ready', config });
        }
      } catch (e) {
        // Only surface an error if we never loaded — a transient poll failure keeps
        // the current overlay rather than blanking it.
        if (!cancelled && !lastJson) setState({ kind: 'error', message: e instanceof Error ? e.message : 'Failed to load overlay.' });
      }
    };
    void load();
    // Poll every 3s so a builder edit (auto-published ~1.5s after you change it)
    // shows on the open overlay within a few seconds — a live mirror, no re-publish.
    // The JSON-diff guard above means an unchanged poll never re-renders, and the
    // config row is tiny, so a frequent poll is cheap.
    const interval = setInterval(load, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id, demo, live]);

  // Full-viewport transparent stage. The chat renderer + live feed mount here in
  // the next phase; for now it confirms the config resolved and stays out of the
  // way (a tiny status note, not a full panel, so a live scene isn't cluttered).
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        background: 'transparent',
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {demo && (() => {
        // Optional diagnostic style overrides, e.g. ?demo=1&style={"showReplies":false}
        const overrides = (() => {
          try { return JSON.parse(searchParams.get('style') ?? '{}') as Record<string, unknown>; } catch { return {}; }
        })();
        const s = clampOverlayStyle({ ...DEFAULT_OVERLAY_STYLE, eventStyle: 'streamnook', ...overrides });
        return (
          <div style={{ width: `${s.width}px`, height: `${s.height}px`, position: 'relative', overflow: 'hidden' }}>
            <OverlayChat messages={DEMO_MESSAGES} style={s} />
          </div>
        );
      })()}
      {live && (
        <HostedOverlayFeed
          overlayId="demo"
          channels={[{ provider: 'twitch', channel: live }]}
          style={searchParams.get('bots') === 'hide' ? { hideBots: true } : {}}
        />
      )}
      {!demo && !live && state.kind === 'error' && (
        <div style={statusStyle}>
          <span style={{ color: '#ff9b9b' }}>{state.message}</span>
        </div>
      )}
      {!demo && !live && state.kind === 'ready' && (
        <HostedOverlayFeed
          overlayId={state.config.id}
          channels={state.config.channels}
          style={state.config.style}
        />
      )}
    </div>
  );
}

const statusStyle: React.CSSProperties = {
  position: 'absolute',
  left: 12,
  bottom: 10,
  fontSize: 12,
  color: 'rgba(255,255,255,0.75)',
  textShadow: '0 1px 2px rgba(0,0,0,0.85)',
  pointerEvents: 'none',
  maxWidth: 'calc(100% - 24px)',
};
